import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { M12_ROLES, hasAnyRole } from '../../lib/roles';
import type { CompetentAuthorityRule, OriginCriterion, Page, TariffSchemeRule } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, PageHeader, Spinner, StatusBadge, Tabs } from '../../ui/components';

const CRITERIA: OriginCriterion[] = ['WHOLLY_OBTAINED', 'CHANGE_TARIFF_HEADING', 'VALUE_ADDED'];

const TABS = [
  { key: 'authority', label: 'Competent-authority rules' },
  { key: 'tariff', label: 'Tariff-scheme rules' },
] as const;

/** Public config reference tables — no sign-in required. Traders can see exactly what gates their permit/certificate. */
export function TradeReference() {
  const { persona } = useAuth();
  const isTradeOfficer = hasAnyRole(persona?.roles, M12_ROLES.TRADE_OFFICER);
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('authority');

  return (
    <div>
      <PageHeader title="Trade Reference Tables" subtitle="Which HS chapters need a competent-authority referral, and which origin criteria each tariff scheme accepts." />
      <Tabs tabs={TABS} value={tab} onChange={setTab} />
      {tab === 'authority' ? <AuthorityRules isOfficer={isTradeOfficer} /> : <TariffRules isOfficer={isTradeOfficer} />}
    </div>
  );
}

function AuthorityRules({ isOfficer }: { isOfficer: boolean }) {
  const [showForm, setShowForm] = useState(false);
  const rulesQ = useApiQuery<Page<CompetentAuthorityRule>>('/api/v1/m12/competent-authority-rules?limit=100');

  return (
    <div>
      {isOfficer ? (
        <div className="mb-3 text-right">
          <button className="btn-primary" onClick={() => setShowForm((s) => !s)}>+ Add / update rule</button>
        </div>
      ) : null}
      {showForm ? <AuthorityRuleForm onDone={() => { setShowForm(false); rulesQ.reload(); }} /> : null}
      {rulesQ.error ? <ErrorNote message={rulesQ.error} /> : null}
      <div className="card overflow-x-auto">
        {rulesQ.loading ? <Spinner /> : (rulesQ.data?.items.length ?? 0) === 0 ? <EmptyState title="No rules configured" /> : (
          <table className="w-full">
            <thead><tr><th className="table-head">HS chapter</th><th className="table-head">Authority</th><th className="table-head">Mandatory</th><th className="table-head">Status</th></tr></thead>
            <tbody>
              {rulesQ.data!.items.map((r) => (
                <tr key={r.id}>
                  <td className="table-cell font-mono text-xs">{r.hsChapter}</td>
                  <td className="table-cell">{r.authorityName} <span className="text-xs text-slate-400">({r.authorityCode})</span></td>
                  <td className="table-cell">{r.mandatory ? 'Yes' : 'No'}</td>
                  <td className="table-cell"><StatusBadge status={r.active ? 'ACTIVE' : 'CLOSED'} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function AuthorityRuleForm({ onDone }: { onDone: () => void }) {
  const [hsChapter, setHsChapter] = useState('');
  const [authorityCode, setAuthorityCode] = useState('');
  const [authorityName, setAuthorityName] = useState('');
  const [mandatory, setMandatory] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m12/competent-authority-rules', { hsChapter, authorityCode: authorityCode.toUpperCase(), authorityName, mandatory, active: true });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not save the rule');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card mb-4 p-4">
      {error ? <ErrorNote message={error} /> : null}
      <div className="grid grid-cols-4 gap-2">
        <Field label="HS chapter (2 digits)" className="!mb-0"><input className="field-input" value={hsChapter} onChange={(e) => setHsChapter(e.target.value)} placeholder="03" /></Field>
        <Field label="Authority code" className="!mb-0"><input className="field-input" value={authorityCode} onChange={(e) => setAuthorityCode(e.target.value)} placeholder="SFA" /></Field>
        <Field label="Authority name" className="!mb-0"><input className="field-input" value={authorityName} onChange={(e) => setAuthorityName(e.target.value)} placeholder="Seychelles Fishing Authority" /></Field>
        <label className="mt-6 flex items-center gap-2 text-sm"><input type="checkbox" checked={mandatory} onChange={(e) => setMandatory(e.target.checked)} /> Mandatory</label>
      </div>
      <button className="btn-primary mt-2 !py-1 text-xs" onClick={submit} disabled={busy || !/^\d{2}$/.test(hsChapter) || authorityCode.length < 2 || authorityName.length < 2}>{busy ? 'Saving…' : 'Save rule'}</button>
    </div>
  );
}

function TariffRules({ isOfficer }: { isOfficer: boolean }) {
  const [showForm, setShowForm] = useState(false);
  const rulesQ = useApiQuery<Page<TariffSchemeRule>>('/api/v1/m12/tariff-scheme-rules?limit=100');

  return (
    <div>
      {isOfficer ? (
        <div className="mb-3 text-right">
          <button className="btn-primary" onClick={() => setShowForm((s) => !s)}>+ Add / update rule</button>
        </div>
      ) : null}
      {showForm ? <TariffRuleForm onDone={() => { setShowForm(false); rulesQ.reload(); }} /> : null}
      {rulesQ.error ? <ErrorNote message={rulesQ.error} /> : null}
      <div className="card overflow-x-auto">
        {rulesQ.loading ? <Spinner /> : (rulesQ.data?.items.length ?? 0) === 0 ? <EmptyState title="No rules configured" /> : (
          <table className="w-full">
            <thead><tr><th className="table-head">Scheme</th><th className="table-head">HS chapter</th><th className="table-head">Allowed criteria</th><th className="table-head">Min local VA %</th><th className="table-head">Status</th></tr></thead>
            <tbody>
              {rulesQ.data!.items.map((r) => (
                <tr key={r.id}>
                  <td className="table-cell font-semibold">{r.scheme}</td>
                  <td className="table-cell font-mono text-xs">{r.hsChapter}</td>
                  <td className="table-cell text-xs">{r.allowedCriteria.join(', ')}</td>
                  <td className="table-cell text-xs">{r.minLocalValueAddedPct ?? '—'}</td>
                  <td className="table-cell"><StatusBadge status={r.active ? 'ACTIVE' : 'CLOSED'} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function TariffRuleForm({ onDone }: { onDone: () => void }) {
  const [scheme, setScheme] = useState<'AFCFTA' | 'GSP' | 'EU'>('AFCFTA');
  const [hsChapter, setHsChapter] = useState('');
  const [allowed, setAllowed] = useState<OriginCriterion[]>(['WHOLLY_OBTAINED']);
  const [minPct, setMinPct] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (c: OriginCriterion) => setAllowed((a) => (a.includes(c) ? a.filter((x) => x !== c) : [...a, c]));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m12/tariff-scheme-rules', {
        scheme,
        hsChapter,
        allowedCriteria: allowed,
        minLocalValueAddedPct: allowed.includes('VALUE_ADDED') && minPct ? Number.parseFloat(minPct) : undefined,
        active: true,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not save the rule');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card mb-4 p-4">
      {error ? <ErrorNote message={error} /> : null}
      <div className="grid grid-cols-3 gap-2">
        <Field label="Scheme" className="!mb-0">
          <select className="field-input" value={scheme} onChange={(e) => setScheme(e.target.value as typeof scheme)}>
            <option value="AFCFTA">AfCFTA</option>
            <option value="GSP">GSP</option>
            <option value="EU">EU</option>
          </select>
        </Field>
        <Field label="HS chapter" className="!mb-0"><input className="field-input" value={hsChapter} onChange={(e) => setHsChapter(e.target.value)} placeholder="61" /></Field>
        {allowed.includes('VALUE_ADDED') ? (
          <Field label="Min local value-added %" className="!mb-0"><input className="field-input" type="number" value={minPct} onChange={(e) => setMinPct(e.target.value)} /></Field>
        ) : null}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {CRITERIA.map((c) => (
          <label key={c} className={`flex cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium ${allowed.includes(c) ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-300 text-slate-600'}`}>
            <input type="checkbox" className="hidden" checked={allowed.includes(c)} onChange={() => toggle(c)} /> {c.replaceAll('_', ' ')}
          </label>
        ))}
      </div>
      <button className="btn-primary mt-2 !py-1 text-xs" onClick={submit} disabled={busy || !/^\d{2}$/.test(hsChapter) || allowed.length === 0}>{busy ? 'Saving…' : 'Save rule'}</button>
    </div>
  );
}
